import { Tab } from '@headlessui/react';
import cn from 'classnames';
import { FC, memo } from 'react';
import { useTranslation as t } from '@utils/hooks';
import { SubscriptionProductUpgradeOption } from 'fragile/types';
import Divider from '../../../components/Divider';
import IconDynamic from '../../../components/IconDynamic';

export enum ConditionOption {
  GUARANTEED_NEW = 'GUARANTEED_NEW',
  LIKE_NEW = 'LIKE_NEW',
}

type TabData = {
  title: string;
  description: string;
  body?: string[];
  price?: string;
  iconName: string;
  conditionType: ConditionOption;
};

type PdpConditionSelectorProps = {
  selectedConditionOption: ConditionOption;
  setConditionOption: (option: ConditionOption) => void;
  selectedUpgradeOption: SubscriptionProductUpgradeOption;
};

const PdpConditionSelector: FC<PdpConditionSelectorProps> = ({
  selectedConditionOption,
  setConditionOption,
  selectedUpgradeOption,
}) => {
  const CHOOSE_TITLE = t('subscription_pdp_choose_condition');
  const GUARANTEED_TITLE = t('subscription_pdp_guaranteed_condition');
  const MINIMUM_TITLE = t('subscription_pdp_minimum_condition');

  // Calculate savings dynamically based on the difference between setupFeeUpgradePrice and setupFeePrice
  const calculateSavings = (): string => {
    const setupFeeUpgradePrice =
      selectedUpgradeOption.setupFeeUpgrade?.options[0]?.price;
    if (
      setupFeeUpgradePrice &&
      typeof setupFeeUpgradePrice.amount === 'number'
    ) {
      const savingsAmount = setupFeeUpgradePrice.amount;
      if (savingsAmount > 0) {
        return `Save $${Math.floor(savingsAmount / 100)}`;
      }
    }
    return '';
  };

  const conditionTabs: TabData[] = [
    {
      title: `Like-New — ${MINIMUM_TITLE}`,
      description:
        'A refurbished PC that meets or exceeds the listed parts and performance.',
      body: [
        'Thoroughly tested and restored to like-new condition',
        "Parts may vary but will always meet or exceed what's shown",
        'FPS is at or above the listed minimums for popular games',
      ],
      price: calculateSavings(),
      iconName: 'IconReinforcedExtendedTubing',
      conditionType: ConditionOption.LIKE_NEW,
    },
    {
      title: `New — ${GUARANTEED_TITLE}`,
      description:
        'A brand-new PC with the exact parts and performance listed.',
      body: [
        'Fresh from the factory and never used',
        'Includes the exact CPU, GPU, RAM, storage, and case shown',
        'FPS matches listed averages for popular games',
      ],

      iconName: 'IconGiveaway',
      conditionType: ConditionOption.GUARANTEED_NEW,
    },
  ];

  // Get the index of the selected condition option
  const selectedIndex = conditionTabs.findIndex(
    tab => tab.conditionType === selectedConditionOption
  );

  return (
    <div className="w-full">
      <div className="text-lg font-semibold mb-4">{CHOOSE_TITLE}</div>
      <Tab.Group
        selectedIndex={selectedIndex !== -1 ? selectedIndex : 0}
        onChange={index => {
          setConditionOption(conditionTabs[index].conditionType);
        }}
      >
        <Tab.List className="flex flex-col gap-4">
          {conditionTabs.map(tab => (
            <Tab
              key={tab.title}
              className={({ selected }) =>
                cn(
                  'w-full rounded-lg py-4 px-3 cursor-pointer border focus-visible:outline-none transition text-left',
                  {
                    'shadow-activeBorder border-transparent': selected,
                    'border-nzxt-light-grey-300 hover:border-gray-300':
                      !selected,
                  }
                )
              }
            >
              {({ selected }) => (
                <div className="flex flex-col text-left w-full">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2">
                      <IconDynamic
                        iconName={tab.iconName}
                        className="w-5 h-5 text-gray-700"
                      />
                      <span className="font-semibold text-sm">{tab.title}</span>
                    </div>
                    {tab.price && (
                      <div className="text-sm font-semibold">{tab.price}</div>
                    )}
                  </div>

                  <p className="text-gray-600 text-xs py-2">
                    {tab.description}
                  </p>

                  {selected && tab.body && (
                    <>
                      <div className="py-1">
                        <Divider />
                      </div>
                      <ul className="list-disc px-4 mt-2 text-sm">
                        {tab.body.map(spec => (
                          <li key={spec} className="text-gray-600 py-0.5">
                            {spec}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </Tab>
          ))}
        </Tab.List>
      </Tab.Group>
    </div>
  );
};

export default memo(PdpConditionSelector);
